import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #selectionSequence = 0;
  #requestedMediaId = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#selectionSequence += 1;
    this.#requestedMediaId = null;
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (event.target.closest('.shopify-section, dialog') !== this.closest('.shopify-section, dialog')) return;
    const sequence = ++this.#selectionSequence;
    const requestedMediaId = this.#requestedMediaId;
    this.#requestedMediaId = null;

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || sequence !== this.#selectionSequence || !detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (this.hasAttribute('data-sync-variant-media') &&
            newMediaGallery.hasAttribute('data-sync-variant-media') &&
            this.dataset.productId === newMediaGallery.dataset.productId) {
          const mediaOrder = JSON.parse(this.dataset.mediaOrder || '[]').map(String);
          const nextOrder = JSON.parse(newMediaGallery.dataset.mediaOrder || '[]').map(String);
          if (mediaOrder.length === nextOrder.length && nextOrder.every((id) => mediaOrder.includes(id))) {
            this.dataset.selectedVariantId = newMediaGallery.dataset.selectedVariantId;
            this.dataset.selectedMediaId = newMediaGallery.dataset.selectedMediaId;
            const variants = JSON.parse(this.querySelector('script[data-variant-media]')?.textContent || '[]');
            const keepRequestedMedia = variants.some((variant) =>
              variant.id === this.dataset.selectedVariantId && variant.mediaId === requestedMediaId
            );
            const index = mediaOrder.indexOf(keepRequestedMedia ? requestedMediaId : this.dataset.selectedMediaId);
            if (index !== -1) this.slideshow?.select(index, undefined, { animate: false });
            return;
          }
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #handleSlideSelect = (event) => {
    if (!this.hasAttribute('data-sync-variant-media') || event.target !== this.slideshow || !event.detail.userInitiated) return;
    const mediaOrder = JSON.parse(this.dataset.mediaOrder || '[]').map(String);
    const mediaId = mediaOrder[event.detail.index];
    if (!mediaId) return;
    const variants = JSON.parse(this.querySelector('script[data-variant-media]')?.textContent || '[]');
    const matchingVariants = variants.filter((variant) => variant.mediaId === mediaId);
    if (!matchingVariants.length) return;
    const scope = this.closest('.shopify-section, dialog');
    const picker = Array.from(scope?.querySelectorAll('variant-picker') || [])
      .find((element) => element.dataset.productId === this.dataset.productId);
    if (!picker) return;
    const controls = Array.from(picker.querySelectorAll('input[type="radio"], option'));
    const matches = controls.filter((control) => matchingVariants.some((variant) => variant.id === control.dataset.variantId));
    if (matches.some((control) => control instanceof HTMLInputElement ? control.checked : control.selected)) return;
    const control = matches[0];
    if (control instanceof HTMLInputElement) {
      if (control.disabled) return;
      this.#requestedMediaId = mediaId;
      control.click();
    } else if (control instanceof HTMLOptionElement && control.parentElement instanceof HTMLSelectElement) {
      const select = control.parentElement;
      if (control.disabled || select.disabled) return;
      this.#requestedMediaId = mediaId;
      select.value = control.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

